import React, { useState } from 'react';

import {
  FormControl,
  MenuItem,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Select,
  Checkbox,
} from '@material-ui/core';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { makeStyles } from '@material-ui/core/styles';

import { UiButton } from './ui-button';

type InputProps = {
  label: string;
  filterValueAndOptions: Array<SelectOption<string>>;
  onFilterValueChange: (selectedFilterOptions: Array<SelectOption<string>>) => void;
  className?: string;
  selectMinWidth?: number;
};

// Duplicated in ui-filter-select
// Creates a gap in the select border where the floating label is displayed
const useStyles = makeStyles(() => ({
  outlinedInput: {
    fontSize: '13px',
    padding: '6px 10px 5px 10px',
  },
  notchedOutline: {
    '& > legend': {
      fontSize: 'calc(13px * .75)',
    },
  },
  outlinedLabel: {
    fontSize: '13px',
    transform: 'translate(10px, 9px) scale(1)',
    '&.shrink': {
      transform: 'translate(14px, -5px) scale(.75)',
    },
  },
}));

export const UiMultiselectFilter = (props: InputProps) => {
  const { label, filterValueAndOptions, onFilterValueChange, className, selectMinWidth } = props;
  const allEventTypesSelected = filterValueAndOptions.every((opt) => opt.selected);
  const noEventTypesSelected = filterValueAndOptions.every((opt) => !opt.selected);

  // Manage menu open state in this component instead of automatically
  // We need this to make the menu work in a popover component
  const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>(false);

  const classes = useStyles();

  const handleChangeFilter = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    // Keep menu open when we change options
    event.stopPropagation();

    const eventValues = event.target.value;
    if (!Array.isArray(eventValues)) {
      return;
    }
    if (eventValues[eventValues.length - 1] === 'ALL') {
      onFilterValueChange(
        filterValueAndOptions.map((opt) => ({ ...opt, selected: !allEventTypesSelected })),
      );
      return;
    }
    onFilterValueChange(
      filterValueAndOptions.map((opt) => ({ ...opt, selected: eventValues.includes(opt.value) })),
    );
  };

  const handleOnlyClick =
    (option: SelectOption<string>) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      // Keep menu open when we change options
      event.stopPropagation();
      onFilterValueChange(
        filterValueAndOptions.map((opt) => ({ ...opt, selected: option.value === opt.value })),
      );
    };

  const setMenuOpen = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    setIsSelectorOpen(!isSelectorOpen);
  };

  const placeholder = `Filter ${label}`;
  const selectedOptions = filterValueAndOptions
    .filter((opt) => opt.selected)
    .map((opt) => opt.label);

  return (
    <FormControl variant="outlined" className={className}>
      <InputLabel classes={{ outlined: classes.outlinedLabel }}>{placeholder}</InputLabel>
      <Select
        classes={{ outlined: classes.outlinedInput }}
        multiple
        value={filterValueAndOptions.filter((opt) => opt.selected).map((opt) => opt.value)}
        onChange={handleChangeFilter}
        open={isSelectorOpen}
        input={
          <OutlinedInput
            label={placeholder}
            classes={{ notchedOutline: classes.notchedOutline }}
            onClick={setMenuOpen}
          />
        }
        renderValue={() => {
          if (selectedOptions.length === filterValueAndOptions.length) {
            return `Any ${label}`;
          }
          return selectedOptions.join(', ');
        }}
        MenuProps={{
          getContentAnchorEl: null,
        }}
        style={{ fontSize: 'small', height: 32, minWidth: selectMinWidth }}
      >
        <MenuItem key="ALL" value="ALL">
          <ListItemIcon>
            <Checkbox
              checked={allEventTypesSelected}
              indeterminate={!allEventTypesSelected && !noEventTypesSelected}
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />
        </MenuItem>
        {filterValueAndOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <ListItemIcon>
              <Checkbox checked={opt.selected} />
            </ListItemIcon>
            <ListItemText primary={opt.label} />
            <UiButton
              size="xxs"
              variant="tertiary"
              className="u-margin-left"
              onClick={handleOnlyClick(opt)}
            >
              Only
            </UiButton>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
